import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, forkJoin } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CallCenterStatsService } from './call-center-stats.service';
import { SalesStatsService } from './sales-stats.service';
import { DateService } from './date.service';
import { ChangeDetectorRef } from '@angular/core';
import { CallCenterStatsData, UpdateNotesRequest } from '../interfaces/call-center-stats.interface';
import { SalesStats, UpdateNotesRequest as SalesUpdateNotesRequest } from '../interfaces/sales-stats.interface';

/**
 * NotesManagerService
 * 
 * A StatisticsTableComponent jegyzet kezelési logikájáért felelős service.
 * Kezeli a jegyzetek CRUD műveleteit és az adatbázis szinkronizációt.
 */
@Injectable()
export class NotesManagerService {

  // Jegyzetek állapot változó (napi index -> jegyzet szöveg)
  private _notes = new BehaviorSubject<{ [dayIndex: number]: string }>({});
  
  // Publikus observable a jegyzetek megfigyelésére
  public readonly notes$ = this._notes.asObservable();

  constructor(
    private callCenterStatsService: CallCenterStatsService,
    private salesStatsService: SalesStatsService,
    private dateService: DateService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Jelenlegi jegyzetek getter
   */
  get currentNotes(): { [dayIndex: number]: string } {
    return this._notes.value;
  }

  /**
   * Van-e jegyzet az adott naphoz
   */
  hasNoteForDay(dayIndex: number): boolean {
    const notes = this.currentNotes;
    return !!(notes[dayIndex] && notes[dayIndex].trim() !== '');
  }

  /**
   * Új jegyzet hozzáadása
   */
  addNote(dayIndex: number, note: string): void {
    const currentNotes = { ...this.currentNotes };
    currentNotes[dayIndex] = note;
    this._notes.next(currentNotes);
  }

  /**
   * Jegyzet törlése
   */
  deleteNote(dayIndex: number): void {
    const currentNotes = { ...this.currentNotes };
    currentNotes[dayIndex] = '';
    this._notes.next(currentNotes);
  }

  /**
   * Adott nap jegyzetének lekérése
   */
  getNoteForDay(dayIndex: number): string {
    const notes = this.currentNotes;
    return notes[dayIndex] || '';
  }

  /**
   * Jegyzetek törlése (reset)
   */
  clearNotes(): void {
    this._notes.next({});
  }

  /**
   * Jegyzetek betöltése előre betöltött (bulk) statisztikai adatokból.
   * Nem indít külön API hívást, hanem a már meglévő CallCenterStatsData[] tömb
   * notes mezőjéből dolgozik.
   */
  loadNotesFromPreloadedStats(
    preloadedStats: CallCenterStatsData[],
    year: number,
    daysInMonth: number,
    getMonthIndex: () => number,
    cdr: ChangeDetectorRef
  ): void {
    const notes: { [dayIndex: number]: string } = {};

    preloadedStats.forEach(weekData => {
      if (weekData.notes && Array.isArray(weekData.notes)) {
        weekData.notes.forEach((note, weekDayIndex) => {
          if (note && note.trim() !== '' && weekDayIndex >= 0 && weekDayIndex < 5) {
            const dayIndex = this.getDayIndexForWeekDay(
              weekData.week_number, weekDayIndex, year, getMonthIndex, daysInMonth
            );
            if (dayIndex >= 0 && dayIndex < daysInMonth) {
              notes[dayIndex] = note;
            }
          }
        });
      }
    });

    this._notes.next(notes);
    cdr.detectChanges();
  }

  /**
   * Jegyzetek betöltése előre betöltött KÜM (bulk) statisztikai adatokból.
   * Nem indít külön API hívást, hanem a már meglévő SalesStats[] tömb
   * notes mezőjéből dolgozik.
   */
  loadNotesFromPreloadedKumStats(
    preloadedStats: SalesStats[],
    year: number,
    daysInMonth: number,
    getMonthIndex: () => number,
    cdr: ChangeDetectorRef
  ): void {
    const notes: { [dayIndex: number]: string } = {};

    preloadedStats.forEach(weekData => {
      if (weekData.notes && Array.isArray(weekData.notes)) {
        weekData.notes.forEach((note, weekDayIndex) => {
          if (note && note.trim() !== '' && weekDayIndex >= 0 && weekDayIndex < 5) {
            const dayIndex = this.getDayIndexForWeekDay(
              weekData.week_number!, weekDayIndex, year, getMonthIndex, daysInMonth
            );
            if (dayIndex >= 0 && dayIndex < daysInMonth) {
              notes[dayIndex] = note;
            }
          }
        });
      }
    });

    this._notes.next(notes);
    cdr.detectChanges();
  }

  /**
   * Jegyzetek betöltése az adatbázisból
   */
  loadNotesFromDatabase(
    userId: number,
    year: number,
    month: string,
    daysInMonth: number,
    isKumMode: boolean,
    getMonthNumber: () => number,
    getMonthIndex: () => number,
    cdr: ChangeDetectorRef
  ): void {
    if (!userId || userId === 0) return;

    const monthNumber = getMonthNumber();
    if (monthNumber === -1) return;

    const currentDate = this.dateService.getCurrentDate();
    const isDebugMode = this.dateService.isDebugMode();
    const yearToUse = isDebugMode ? currentDate.getFullYear() : year;

    if (isKumMode) {
      // KÜM mód: Sales statisztikák Jegyzetek betöltése
      this.salesStatsService.getMonthStats(userId, monthNumber, yearToUse).subscribe({
        next: (weeklyStats: SalesStats[]) => {
          const notes: { [dayIndex: number]: string } = {};
          
          weeklyStats.forEach(weekData => {
            if (weekData.notes && Array.isArray(weekData.notes)) {
              // Minden hét napjait végigmegyük
              weekData.notes.forEach((note, weekDayIndex) => {
                if (note && note.trim() !== '' && weekDayIndex >= 0 && weekDayIndex < 5) {
                  // Megkeressük melyik napi indexre esik ez a hét napja
                  const dayIndex = this.getDayIndexForWeekDay(weekData.week_number!, weekDayIndex, yearToUse, getMonthIndex, daysInMonth);
                  
                  if (dayIndex >= 0 && dayIndex < daysInMonth) {
                    notes[dayIndex] = note;
                  }
                }
              });
            }
          });
          
          this._notes.next(notes);
          cdr.detectChanges();
        },
        error: (error) => {
          // Silent error handling
        }
      });
    } else {
      // Call Center mód: Call Center statisztikák Jegyzetek betöltése
      this.callCenterStatsService.getMonthStats(userId, monthNumber, yearToUse).subscribe({
        next: (weeklyStats: CallCenterStatsData[]) => {
          const notes: { [dayIndex: number]: string } = {};
          
          weeklyStats.forEach(weekData => {
            if (weekData.notes && Array.isArray(weekData.notes)) {
              // Minden hét napjait végigmegyük
              weekData.notes.forEach((note, weekDayIndex) => {
                if (note && note.trim() !== '' && weekDayIndex >= 0 && weekDayIndex < 5) {
                  // Megkeressük melyik napi indexre esik ez a hét napja
                  const dayIndex = this.getDayIndexForWeekDay(weekData.week_number, weekDayIndex, yearToUse, getMonthIndex, daysInMonth);
                  
                  if (dayIndex >= 0 && dayIndex < daysInMonth) {
                    notes[dayIndex] = note;
                  }
                }
              });
            }
          });
          
          this._notes.next(notes);
          cdr.detectChanges();
        },
        error: (error) => {
          // Silent error handling
        }
      });
    }
  }

  /**
   * Jegyzetek mentése az adatbázisba
   */
  saveNotesToDatabase(
    userId: number,
    year: number,
    daysInMonth: number,
    isKumMode: boolean,
    getMonthNumber: () => number,
    getMonthIndex: () => number,
    getWeekNumber: (date: Date) => number,
    isWeekday: (dayIndex: number) => boolean,
    changedDayIndex?: number,
    isDelete = false
  ): void {
    if (!userId || userId === 0) return;

    const currentDate = this.dateService.getCurrentDate();
    const isDebugMode = this.dateService.isDebugMode();
    const yearToUse = isDebugMode ? currentDate.getFullYear() : year;

    const notes = this.currentNotes;

    // Meghatározzuk a módosított nap hetét
    const targetDate = new Date(yearToUse, getMonthIndex(), (changedDayIndex ?? 0) + 1);
    const targetWeekNumber = changedDayIndex !== undefined ? getWeekNumber(targetDate) : null;

    // Csak az érintett hét napjait dolgozzuk fel
    const weekNotesMap = new Map<number, (string | null)[]>();
    // Tört hét kezelés: nyilvántartja, hogy az adott hétnél melyik weekDayIndexek (0-4)
    // tartoznak az aktuális hónaphoz — csak ezek kerülnek felülírásra az adatbázisban
    const weekActiveDaysMap = new Map<number, number[]>();

    for (let dayIndex = 0; dayIndex < daysInMonth; dayIndex++) {
      if (!isWeekday(dayIndex)) continue;

      const date = new Date(yearToUse, getMonthIndex(), dayIndex + 1);
      const weekNumber = getWeekNumber(date);

      // Ha tudjuk melyik hetet kell menteni, csak azt dolgozzuk fel
      if (targetWeekNumber !== null && weekNumber !== targetWeekNumber) continue;

      const dayOfWeek = date.getDay();
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        const weekDayIndex = dayOfWeek - 1;
        if (!weekNotesMap.has(weekNumber)) {
          weekNotesMap.set(weekNumber, new Array(5).fill(null));
          weekActiveDaysMap.set(weekNumber, []);
        }
        weekNotesMap.get(weekNumber)![weekDayIndex] = notes[dayIndex] || null;
        weekActiveDaysMap.get(weekNumber)!.push(weekDayIndex);
      }
    }

    // Összegyűjtjük az összes API hívást, majd egyszerre várjuk meg őket
    const saveRequests: Observable<any>[] = [];

    weekNotesMap.forEach((weekNotes, weekNumber) => {
      const activeDayIndices = weekActiveDaysMap.get(weekNumber);
      if (isKumMode) {
        const salesUpdateRequest: SalesUpdateNotesRequest = {
          user_id: userId,
          week_number: weekNumber,
          year: yearToUse,
          notes: weekNotes,
          active_day_indices: activeDayIndices
        };
        saveRequests.push(this.salesStatsService.updateNotes(salesUpdateRequest));
      } else {
        const ccUpdateRequest: UpdateNotesRequest = {
          user_id: userId,
          week_number: weekNumber,
          year: yearToUse,
          notes: weekNotes,
          active_day_indices: activeDayIndices
        };
        saveRequests.push(this.callCenterStatsService.updateNotes(ccUpdateRequest));
      }
    });

    if (saveRequests.length === 0) return;

    const savedWeekNumbers = Array.from(weekNotesMap.keys());
    const weekLabel = savedWeekNumbers.length === 1
      ? `${savedWeekNumbers[0]}. hét`
      : `${savedWeekNumbers.join(', ')}. hét`;
    const label = isKumMode ? `${weekLabel} KÜM jegyzetei` : `${weekLabel} jegyzetei`;

    forkJoin(saveRequests).subscribe({
      next: () => {
        const successMessage = isDelete ? '🗑️ Törlés sikeres!' : `✅ ${label} sikeresen mentve!`;
        this.snackBar.open(successMessage, 'Bezár', {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });
      },
      error: (error: any) => {
        this.snackBar.open(`Hiba történt a ${label} mentése során!`, 'Bezár', {
          duration: 5000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  /**
   * Meghatározza hogy az adott hét adott napja melyik napi indexre esik a hónapban
   * (Kompatibilis a CallCenterStatsService algoritmusával)
   */
  private getDayIndexForWeekDay(
    weekNumber: number, 
    weekDayIndex: number, 
    year: number,
    getMonthIndex: () => number,
    daysInMonth: number
  ): number {
    try {
      // ISO hét alapján keressük meg a hét hétfőjét
      // ISO week algoritmus: az első csütörtöktől számítjuk a heteket
      const jan4 = new Date(year, 0, 4);
      const week1Monday = new Date(jan4.getTime() - ((jan4.getDay() + 6) % 7) * 86400000);
      
      // A keresett hét hétfőjének dátuma
      const targetMonday = new Date(week1Monday.getTime() + (weekNumber - 1) * 7 * 86400000);
      
      // A keresett nap dátuma (hétfő + weekDayIndex)
      const targetDate = new Date(targetMonday.getTime() + weekDayIndex * 86400000);
      
      // Ellenőrizzük hogy a dátum az aktuális hónapba esik-e
      if (targetDate.getFullYear() === year && targetDate.getMonth() === getMonthIndex()) {
        const dayOfMonth = targetDate.getDate();
        
        // Ellenőrizzük hogy a nap a hónap határain belül van-e
        if (dayOfMonth >= 1 && dayOfMonth <= daysInMonth) {
          return dayOfMonth - 1; // 0-indexelve
        }
      }
    } catch (error) {
      // Silent error handling
    }
    
    return -1; // Nem esik a hónapba vagy hiba történt
  }

  /**
   * Jegyzetek állapot alaphelyzetbe állítása
   */
  reset(): void {
    this.clearNotes();
  }
}